import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { adminApi } from "../api/client";
import { AdminUser, AuthPayload } from "../types";

interface AdminAuthContextValue {
  user: AdminUser | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

function readStoredAuth(): AuthPayload | null {
  const raw = window.localStorage.getItem(adminApi.authStorageKey);
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthPayload;
  } catch (error) {
    return null;
  }
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      const stored = readStoredAuth();

      if (!stored?.token) {
        setLoading(false);
        return;
      }

      setUser(stored.user);
      setToken(stored.token);

      try {
        const currentUser = await adminApi.me();
        const payload = { token: stored.token, user: currentUser };
        window.localStorage.setItem(adminApi.authStorageKey, JSON.stringify(payload));
        setUser(currentUser);
      } catch (error) {
        window.localStorage.removeItem(adminApi.authStorageKey);
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    void bootstrap();
  }, []);

  const value = useMemo<AdminAuthContextValue>(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: Boolean(user && token),
      login: async (email: string, password: string) => {
        const payload = await adminApi.login({ email, password });
        window.localStorage.setItem(adminApi.authStorageKey, JSON.stringify(payload));
        setUser(payload.user);
        setToken(payload.token);
      },
      logout: () => {
        window.localStorage.removeItem(adminApi.authStorageKey);
        setUser(null);
        setToken(null);
      }
    }),
    [loading, token, user]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);

  if (!context) {
    throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  }

  return context;
}

